/**
 * PLATFORM-1 · Phase 5 · Q5 — Event Monitor
 *
 * Super-Admin surface at `/platform/events`. Shows every domain event flowing
 * through the platform. Live-tail via SSE + rich filters + detail drawer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, Radio, RefreshCw, Search, X } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MODULES = [
  "front_office", "guest", "housekeeping", "finance", "supply",
  "kitchen", "fnb", "maintenance", "workflow", "workforce", "auth",
  "platform", "inventory",
];
const SEVERITIES = ["info", "warning", "critical"];
const ANY = "__any__";  // shadcn Select can't use empty string as a value

function sevColor(sev) {
  switch (sev) {
    case "critical": return "bg-red-100 text-red-800 border-red-200";
    case "warning":  return "bg-amber-100 text-amber-800 border-amber-200";
    default:         return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch (_) { return iso; }
}

export default function EventMonitor() {
  // Filters
  const [module, setModule] = useState(ANY);
  const [severity, setSeverity] = useState(ANY);
  const [eventName, setEventName] = useState(ANY);
  const [hotelId, setHotelId] = useState("");
  const [q, setQ] = useState("");
  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [registry, setRegistry] = useState([]);
  // Live tail
  const [live, setLive] = useState(false);
  const esRef = useRef(null);
  // Detail drawer
  const [selected, setSelected] = useState(null);

  const filters = useMemo(() => {
    const p = {};
    if (module !== ANY)     p.module = module;
    if (severity !== ANY)   p.severity = severity;
    if (eventName !== ANY)  p.event_name = eventName;
    if (hotelId)            p.hotel_id = hotelId;
    if (q)                  p.q = q;
    return p;
  }, [module, severity, eventName, hotelId, q]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/events", {
        params: { ...filters, limit: 100 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setNextCursor(data.next_cursor || null);
    } catch (e) {
      console.error("[event-monitor] fetch failed", e);
    } finally { setLoading(false); }
  }, [filters]);

  const fetchMore = useCallback(async () => {
    if (!nextCursor) return;
    try {
      const { data } = await api.get("/platform/events", {
        params: { ...filters, cursor: nextCursor, limit: 100 },
      });
      setItems((prev) => [...prev, ...(data.items || [])]);
      setNextCursor(data.next_cursor || null);
    } catch (e) {
      console.error("[event-monitor] fetch-more failed", e);
    }
  }, [filters, nextCursor]);

  const fetchRegistry = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/events/registry");
      setRegistry(data.items || []);
    } catch (_) { /* no-op */ }
  }, []);

  useEffect(() => { fetchRegistry(); }, [fetchRegistry]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // SSE live tail — opens a new EventSource whenever filters change or Live is toggled ON.
  useEffect(() => {
    if (!live) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return undefined;
    }
    const qs = new URLSearchParams();
    if (module !== ANY)    qs.set("module", module);
    if (severity !== ANY)  qs.set("severity", severity);
    if (hotelId)           qs.set("hotel_id", hotelId);
    if (eventName !== ANY) qs.set("event_name", eventName);
    const url = `${API_BASE}/platform/events/stream?${qs.toString()}`;
    // withCredentials is required so the auth cookie rides on the SSE handshake.
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.onmessage = (msg) => {
      try {
        const evt = JSON.parse(msg.data);
        setItems((prev) => [evt, ...prev].slice(0, 500));
      } catch (_) { /* ignore keepalive */ }
    };
    es.onerror = () => {
      // Browser will auto-retry; we just log.
      console.warn("[event-monitor] SSE error — will retry");
    };
    return () => { es.close(); };
  }, [live, module, severity, hotelId, eventName]);

  const clearFilters = () => {
    setModule(ANY); setSeverity(ANY); setEventName(ANY);
    setHotelId(""); setQ("");
  };

  return (
    <div className="space-y-6" data-testid="event-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-amber-600" strokeWidth={2} />
            <h1 className="text-2xl font-bold tracking-tight">Event Monitor</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Live view of every domain event across the platform.{" "}
            <span className="text-slate-700 font-medium">{total.toLocaleString()}</span> events match your filters.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={live ? "default" : "outline"}
            data-testid="event-monitor-live-toggle"
            onClick={() => setLive((v) => !v)}
            className={live ? "bg-red-600 hover:bg-red-700 text-white" : ""}
          >
            <Radio className={`w-4 h-4 mr-2 ${live ? "animate-pulse" : ""}`} />
            {live ? "Live" : "Go Live"}
          </Button>
          <Button variant="outline" data-testid="event-monitor-refresh" onClick={fetchList}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 bg-white border border-slate-200 rounded-xl">
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="event-monitor-search-input"
              placeholder="event name or summary…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Module</Label>
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger data-testid="event-monitor-module-select" className="mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All modules</SelectItem>
              {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Severity</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger data-testid="event-monitor-severity-select" className="mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All severities</SelectItem>
              {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Event name</Label>
          <Select value={eventName} onValueChange={setEventName}>
            <SelectTrigger data-testid="event-monitor-name-select" className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="max-h-72 overflow-y-auto">
              <SelectItem value={ANY}>Any event</SelectItem>
              {registry.map((r) => (
                <SelectItem key={r.event_name} value={r.event_name}>
                  {r.event_name} ({r.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Hotel</Label>
          <Input
            data-testid="event-monitor-hotel-input"
            placeholder="hotel_id…"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-6 flex justify-end">
          <Button
            variant="ghost" size="sm" data-testid="event-monitor-clear-filters"
            onClick={clearFilters} className="text-slate-500 hover:text-slate-900"
          >
            <X className="w-4 h-4 mr-1" /> Clear filters
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="event-monitor-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
          <div className="col-span-2">Time</div>
          <div className="col-span-3">Event</div>
          <div className="col-span-1">Module</div>
          <div className="col-span-1">Severity</div>
          <div className="col-span-2">Hotel</div>
          <div className="col-span-1">Entity</div>
          <div className="col-span-2">Actor</div>
        </div>
        <ScrollArea className="max-h-[calc(100vh-350px)]">
          {items.length === 0 && !loading && (
            <div className="p-10 text-center text-sm text-slate-400" data-testid="event-monitor-empty">
              No events match your filters.
            </div>
          )}
          {items.map((e) => (
            <button
              type="button"
              key={e._id || e.event_id}
              data-testid={`event-monitor-row-${e.event_id || e._id}`}
              onClick={() => setSelected(e)}
              className="w-full grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-100 text-left text-sm hover:bg-amber-50/40 transition-colors"
            >
              <div className="col-span-2 text-slate-500 font-mono text-xs">{fmtTime(e.occurred_at || e.timestamp)}</div>
              <div className="col-span-3 font-medium text-slate-800 truncate">{e.event_name || e.name}</div>
              <div className="col-span-1 text-slate-500 text-xs truncate">{e.module || "—"}</div>
              <div className="col-span-1">
                <Badge variant="outline" className={`text-xs ${sevColor(e.severity)}`}>{e.severity || "info"}</Badge>
              </div>
              <div className="col-span-2 text-slate-500 text-xs truncate">{e.hotel_id || "—"}</div>
              <div className="col-span-1 text-slate-500 text-xs truncate">{e.entity_type || "—"}</div>
              <div className="col-span-2 text-slate-500 text-xs truncate">{(e.actor && e.actor.username) || e.actor_id || "system"}</div>
            </button>
          ))}
          {nextCursor && !live && (
            <div className="p-3 text-center">
              <Button variant="outline" size="sm" data-testid="event-monitor-load-more" onClick={fetchMore}>
                Load older events
              </Button>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Detail drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full md:max-w-xl overflow-y-auto" data-testid="event-monitor-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3">
                  <Badge variant="outline" className={sevColor(selected.severity)}>
                    {selected.severity || "info"}
                  </Badge>
                  <span className="font-mono">{selected.event_name || selected.name}</span>
                </SheetTitle>
                <SheetDescription>
                  {fmtTime(selected.occurred_at || selected.timestamp)}{" · "}
                  <span className="font-mono">{selected.event_id || selected._id}</span>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4 text-sm">
                <DetailRow label="Module" value={selected.module} />
                <DetailRow label="Version" value={selected.event_version} />
                <DetailRow label="Hotel" value={selected.hotel_id} />
                <DetailRow label="Business date" value={selected.business_date} />
                <DetailRow label="Entity" value={selected.entity_type && selected.entity_id ? `${selected.entity_type} / ${selected.entity_id}` : "—"} />
                <DetailRow label="Guest" value={selected.guest_id} />
                <DetailRow label="Room" value={selected.room_id} />
                <DetailRow label="Reservation" value={selected.reservation_id} />
                <DetailRow label="Stay" value={selected.stay_id} />
                <DetailRow label="Actor" value={(selected.actor && selected.actor.username) || selected.actor_id || "system"} />
                <DetailRow label="Actor role" value={selected.actor && selected.actor.role} />
                <DetailRow label="Summary" value={selected.summary} />
                <DetailRow label="Correlation" value={selected.correlation_id} />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Payload</div>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto font-mono"
                       data-testid="event-monitor-detail-payload">
                    {JSON.stringify(selected.payload || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold min-w-[110px]">{label}</div>
      <div className="text-sm text-slate-800 font-mono text-right break-all">{String(value)}</div>
    </div>
  );
}
